import type { FunctionComponent } from 'react';
import { Redirect } from 'react-router-dom';

import { NotFoundPage } from '@kanda-libs/ks-design-library-new';
import {
  Lead,
  actions,
  createAction,
  createAppDispatchHook,
  createRoutedApp,
} from '@kanda-libs/ks-frontend-services';
import { CreatePageArgs } from '@kanda-libs/ks-frontend-services/src/middleware/helpers';

import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore';
import AddCompanyPage from 'components/company/AddCompanyPage';
import CompaniesListPage from 'components/company/CompaniesListPage';
import CompanyLayout from 'components/company/CompanyLayout';
import HomePage from 'components/home/HomePage';
import JobLayout from 'components/job/JobLayout';
import JobsListPage from 'components/job/JobsListPage';
import LeadLayout from 'components/lead/LeadLayout';
import LeadsListPage from 'components/lead/LeadsListPage';
import LeadsPipeline from 'components/lead/LeadsPipeline';
import LoansLayout from 'components/loan/LoansLayout';
import PartnerLayout from 'components/partner/PartnerLayout';
import PartnersListPage from 'components/partner/PartnersListPage';
import PaymentLayout from 'components/payment/PaymentLayout';
import PaymentsLayout from 'components/payment/PaymentsLayout';
import RateRequestListPage from 'components/rates/RateRequestListPage';
import UsersListPage from 'components/user/UsersListPage';
import Login from 'pages/login';
import {
  type AppDispatch,
  type ExtraState,
  type RootState,
  store,
} from 'store';

import { URLS } from './constants';

import AppWrapper from '../AppWrapper';
import LogoutPage from '../LogoutPage';

export type PageKeys =
  | 'home'
  | 'companies'
  | 'companiesAll'
  | 'companiesCompletedSetup'
  | 'companiesVerified'
  | 'companiesOngoing'
  | 'login'
  | 'company'
  | 'job'
  | 'jobs'
  | 'jobsAll'
  | 'jobsApplied'
  | 'jobsOngoing'
  | 'jobsSatNote'
  | 'jobsSatNoteSigned'
  | 'jobsSatNoteDelayed'
  | 'jobsSatNoteLenderReview'
  | 'jobsPayoutPending'
  | 'jobsPaid'
  | 'payment'
  | 'payments'
  | 'paymentsAll'
  | 'paymentsCash'
  | 'paymentsCard'
  | 'paymentsLoan'
  | 'loans'
  | 'loansAll'
  | 'loansOngoing'
  | 'loansFinished'
  | 'partner'
  | 'partners'
  | 'partnersFilter'
  | 'lead'
  | 'leads'
  | 'leadsPipline'
  | 'leadsOpen'
  | 'leadsIssued'
  | 'leadsCreditDeclined'
  | 'leadsQuoteDeclined'
  | 'leadsTradeAccepted'
  | 'leadsTradeDeclined'
  | 'leadsTradeNotified'
  | 'leadsExpired'
  | 'leadsClosed'
  | 'rateRequests'
  | 'users'
  | 'logout'
  | 'companiesAddCompany';

const monitorsAction = createAction(actions.getMonitors, {
  params: {
    format: 'reduced',
  },
});

const getCompaniesPageProps = (
  pageKey: PageKeys,
  status: 'all' | 'verified' | 'completed_setup' | 'ongoing' = 'verified',
): CreatePageArgs<RootState> => {
  let extraParams = {};
  switch (status) {
    case 'verified':
      extraParams = { q: 'metadata.liveness:true,lifecycle:verified' };
      break;
    case 'completed_setup':
      extraParams = {
        q: 'metadata.liveness:true,lifecycle:completed_setup',
      };
      break;
    case 'ongoing':
      extraParams = {
        q: 'metadata.liveness:true,lifecycle:registered|subscribed|director_info_provided|director_details_submitted|onboarded|insurance_uploaded',
      };
  }

  return {
    path: URLS[pageKey],
    PageComponent: CompaniesListPage,
    requiredRoles: ['staff'],
    loadingDependencies: ['monitor', 'company'],
    initialDataActions: [
      monitorsAction,
      createAction(actions.getCompanies, {
        params: {
          format: 'reduced',
          ...extraParams,
        },
      }),
    ],
  };
};

const getJobsPageProps = (
  pageKey: PageKeys,
  status:
    | 'all'
    | 'ongoing'
    | 'applied'
    | 'satnote'
    | 'satnote_signed'
    | 'satnote_delayed'
    | 'satnote_lender_review'
    | 'payout_pending'
    | 'paid' = 'applied',
): CreatePageArgs<RootState> => {
  let extraParams = {};

  switch (status) {
    case 'ongoing':
      extraParams = {
        q: 'metadata.liveness:true,archived:no,status:sent,finance_status:not_applied',
      };
      break;
    case 'applied':
      extraParams = {
        q: 'metadata.liveness:true,archived:no,finance_status:applied_for_finance|financed',
      };
      break;
    case 'satnote':
      extraParams = {
        q: 'metadata.liveness:true,archived:no,finance_status:sat_note_sent|sat_note_viewed',
      };
      break;
    case 'satnote_signed':
      extraParams = {
        q: 'metadata.liveness:true,archived:no,finance_status:sat_note_signed',
      };
      break;
    case 'satnote_delayed':
      extraParams = {
        q: 'metadata.liveness:true,archived:no,finance_status:sat_note_delayed',
      };
      break;
    case 'satnote_lender_review':
      extraParams = {
        q: 'metadata.liveness:true,archived:no,finance_status:sat_note_lender_review',
      };
      break;
    case 'payout_pending':
      extraParams = {
        q: 'metadata.liveness:true,archived:no,finance_status:payout_pending',
      };
      break;
    case 'paid':
      extraParams = {
        q: 'metadata.liveness:true,finance_status:paid',
      };
      break;
  }

  const initialDataActions =
    status == 'ongoing'
      ? [
          monitorsAction,
          createAction(actions.getJobs, {
            params: {
              format: 'reduced',
              ...extraParams,
            },
          }),
          createAction(actions.getCompanies, {
            params: {
              format: 'reduced',
              q: 'metadata.liveness:true,lifecycle:verified',
            },
          }),
        ]
      : [
          monitorsAction,
          createAction(actions.getJobs, {
            params: {
              format: 'reduced',
              ...extraParams,
            },
          }),
          createAction(actions.getCompanies, {
            params: {
              format: 'reduced',
              q: 'metadata.liveness:true,lifecycle:verified',
            },
          }),
          createAction(actions.getCredits, {
            params: {
              format: 'reduced',
              q: '-state:',
            },
          }),
        ];

  return {
    path: URLS[pageKey],
    PageComponent: JobsListPage,
    requiredRoles: ['staff'],
    loadingDependencies: ['monitor', 'company', 'job', 'credit'],
    initialDataActions: initialDataActions,
  };
};

const getPaymentsPageProps = (
  pageKey: PageKeys,
  status: 'all' | 'cash' | 'card' | 'loan' = 'loan',
): CreatePageArgs<RootState> => {
  let extraParams = {};
  switch (status) {
    case 'cash':
      extraParams = {
        q: 'metadata.liveness:true,flow_type:live,payment_option.payment_method:cash',
      };
      break;
    case 'card':
      extraParams = {
        q: 'metadata.liveness:true,flow_type:live,payment_option.payment_method:card',
      };
      break;
    case 'loan':
      extraParams = {
        q: 'metadata.liveness:true,flow_type:live,payment_option.payment_method:loan',
      };
  }

  return {
    path: URLS[pageKey],
    PageComponent: PaymentsLayout,
    requiredRoles: ['staff'],
    loadingDependencies: ['monitor', 'job', 'payment', 'company'],
    initialDataActions: [
      monitorsAction,
      createAction(actions.getJobs, {
        params: {
          format: 'reduced',
          q: 'metadata.liveness:true,flow_type:live,status:sent|accepted|finished',
        },
      }),
      createAction(actions.getCompanies, {
        params: {
          format: 'reduced',
          q: 'metadata.liveness:true,lifecycle:verified',
        },
      }),
      createAction(actions.getPayments, {
        params: {
          format: 'reduced',
          ...extraParams,
        },
      }),
    ],
  };
};

const getLoansPageProps = (
  pageKey: PageKeys,
  status: 'all' | 'ongoing' | 'finished' = 'ongoing',
): CreatePageArgs<RootState> => {
  let extraParams = {};
  switch (status) {
    case 'ongoing':
      extraParams = {
        q: 'metadata.liveness:true,flow_type:live,state:pending|accepted|referred|action_customer',
      };
      break;
    case 'finished':
      extraParams = {
        q: 'metadata.liveness:true,flow_type:live,state:cancelled|declined|finished',
      };
      break;
  }

  return {
    path: URLS[pageKey],
    PageComponent: LoansLayout,
    requiredRoles: ['staff'],
    loadingDependencies: ['monitor', 'job', 'credit', 'company'],
    initialDataActions: [
      monitorsAction,
      createAction(actions.getJobs, {
        params: {
          format: 'reduced',
          q: 'metadata.liveness:true,flow_type:live,status:sent|accepted|finished',
        },
      }),
      createAction(actions.getCompanies, {
        params: {
          format: 'reduced',
          q: 'metadata.liveness:true,lifecycle:verified',
        },
      }),
      createAction(actions.getCredits, {
        params: {
          format: 'reduced',
          ...extraParams,
        },
      }),
    ],
  };
};

const getLeadsPageProps = (
  pageKey: PageKeys,
  status: Lead['status'] | '',
): CreatePageArgs<RootState> => {
  const extraParams = status ? { q: `status:${status}` } : {};

  return {
    path: URLS[pageKey],
    PageComponent: LeadsListPage,
    requiredRoles: ['staff'],
    loadingDependencies: ['lead'],
    initialDataActions: [
      createAction(actions.getLeads, {
        params: {
          format: 'reduced',
          ...extraParams,
        },
      }),
    ],
  };
};

const {
  pages,
  router: { Router },
} = createRoutedApp<RootState, PageKeys, ExtraState>(
  store as unknown as ToolkitStore<RootState>,
  {
    login: {
      path: URLS.login,
      PageComponent: Login,
      requiredRoles: [],
      loadingDependencies: [],
      initialDataActions: [],
    },
    logout: {
      path: URLS.logout,
      PageComponent: LogoutPage,
      loadingDependencies: [],
      initialDataActions: [],
    },
    home: {
      path: URLS.home,
      PageComponent: HomePage,
      loadingDependencies: [],
      initialDataActions: [],
      requiredRoles: ['staff'],
    },
    companies: getCompaniesPageProps('companies'),
    companiesAll: getCompaniesPageProps('companiesAll', 'all'),
    companiesVerified: getCompaniesPageProps('companiesVerified', 'verified'),
    companiesCompletedSetup: getCompaniesPageProps(
      'companiesCompletedSetup',
      'completed_setup',
    ),
    companiesOngoing: getCompaniesPageProps('companiesOngoing', 'ongoing'),
    company: {
      path: URLS.company,
      PageComponent: CompanyLayout,
      requiredRoles: ['staff'],
      idRequired: true,
      loadingDependencies: ['monitor', 'company', 'infoEntity'],
      initialDataActions: [
        monitorsAction,
        createAction(
          actions.getInfoEntity,
          {
            params: {
              kind: 'company',
            },
          },
          true,
        ),
        createAction(actions.getEnrolment, {}, true),
        createAction(actions.getRate, { params: { id: 'all' } }),
        createAction(actions.getCompanyRate, {}, true),
      ],
    },
    jobs: getJobsPageProps('jobs'),
    jobsAll: getJobsPageProps('jobsAll', 'all'),
    jobsApplied: getJobsPageProps('jobsApplied', 'applied'),
    jobsOngoing: getJobsPageProps('jobsOngoing', 'ongoing'),
    jobsSatNote: getJobsPageProps('jobsSatNote', 'satnote'),
    jobsSatNoteSigned: getJobsPageProps('jobsSatNoteSigned', 'satnote_signed'),
    jobsSatNoteDelayed: getJobsPageProps(
      'jobsSatNoteDelayed',
      'satnote_delayed',
    ),
    jobsSatNoteLenderReview: getJobsPageProps(
      'jobsSatNoteLenderReview',
      'satnote_lender_review',
    ),
    jobsPayoutPending: getJobsPageProps('jobsPayoutPending', 'payout_pending'),
    jobsPaid: getJobsPageProps('jobsPaid', 'paid'),
    job: {
      path: URLS.job,
      PageComponent: JobLayout,
      requiredRoles: ['staff'],
      idRequired: true,
      loadingDependencies: ['monitor', 'job', 'company', 'infoEntity'],
      initialDataActions: [
        monitorsAction,
        createAction(
          actions.getInfoEntity,
          {
            params: {
              kind: 'job',
            },
          },
          true,
        ),
        createAction(actions.checkJob, {}, true),
      ],
    },
    loans: getLoansPageProps('loans'),
    loansAll: getLoansPageProps('loansAll', 'all'),
    loansOngoing: getLoansPageProps('loansOngoing', 'ongoing'),
    loansFinished: getLoansPageProps('loansFinished', 'finished'),
    payments: getPaymentsPageProps('payments'),
    paymentsAll: getPaymentsPageProps('paymentsAll', 'all'),
    paymentsCash: getPaymentsPageProps('paymentsCash', 'cash'),
    paymentsCard: getPaymentsPageProps('paymentsCard', 'card'),
    paymentsLoan: getPaymentsPageProps('paymentsLoan', 'loan'),
    partner: {
      path: URLS.partner,
      PageComponent: PartnerLayout,
      idRequired: true,
      requiredRoles: ['staff'],
      loadingDependencies: ['monitor', 'company', 'partner'],
      initialDataActions: [
        monitorsAction,
        createAction(actions.getPartner, {}, true),
        createAction(actions.getCompanies, {
          params: {
            format: 'reduced',
          },
        }),
      ],
    },
    partners: {
      path: URLS.partners,
      PageComponent: PartnersListPage,
      requiredRoles: ['staff'],
      loadingDependencies: ['monitor', 'company', 'partner'],
      initialDataActions: [
        monitorsAction,
        createAction(actions.getPartners),
        createAction(actions.getCompanies, {
          params: {
            format: 'reduced',
          },
        }),
      ],
    },
    partnersFilter: {
      path: URLS.partnersFilter,
      PageComponent: PartnersListPage,
      idRequired: true,
      requiredRoles: ['staff'],
      loadingDependencies: ['monitor', 'company', 'partner'],
      initialDataActions: [
        monitorsAction,
        createAction(actions.getPartners),
        createAction(actions.getCompanies, {
          params: {
            format: 'reduced',
          },
        }),
      ],
    },
    leads: getLeadsPageProps('leads', ''),
    leadsPipline: {
      path: URLS.leadsPipline,
      PageComponent: LeadsPipeline,
      requiredRoles: ['staff'],
      loadingDependencies: ['lead'],
      initialDataActions: [
        createAction(actions.getLeads, {
          params: {
            format: 'reduced',
          },
        }),
      ],
    },
    leadsOpen: getLeadsPageProps('leadsOpen', 'open'),
    leadsIssued: getLeadsPageProps('leadsIssued', 'issued'),
    leadsCreditDeclined: getLeadsPageProps(
      'leadsCreditDeclined',
      'credit_declined',
    ),
    leadsQuoteDeclined: getLeadsPageProps(
      'leadsQuoteDeclined',
      'quote_declined',
    ),
    leadsTradeAccepted: getLeadsPageProps(
      'leadsTradeAccepted',
      'trade_accepted',
    ),
    leadsTradeDeclined: getLeadsPageProps(
      'leadsTradeDeclined',
      'trade_declined',
    ),
    leadsTradeNotified: getLeadsPageProps(
      'leadsTradeNotified',
      'trade_notified',
    ),
    leadsExpired: getLeadsPageProps('leadsExpired', 'expired'),
    leadsClosed: getLeadsPageProps('leadsClosed', 'closed'),
    lead: {
      path: URLS.lead,
      PageComponent: LeadLayout,
      requiredRoles: ['staff'],
      loadingDependencies: ['lead', 'job', 'company'],
      initialDataActions: [
        createAction(actions.getLead, {}, true, (lead) => [
          createAction(actions.getJobs, {
            params: {
              q: `quoted_to:${lead.data?.id}`,
              format: 'reduced',
            },
          }),
        ]),
      ],
      idRequired: true,
    },
    payment: {
      path: URLS.payment,
      PageComponent: PaymentLayout,
      requiredRoles: ['staff'],
      idRequired: true,
      loadingDependencies: ['monitor', 'payment', 'credit', 'infoEntity'],
      initialDataActions: [
        monitorsAction,
        createAction(actions.infoRate),
        createAction(
          actions.getInfoEntity,
          {
            params: {
              kind: 'payment',
            },
          },
          true,
        ),
      ],
    },
    rateRequests: {
      path: URLS.rateRequests,
      PageComponent: RateRequestListPage,
      requiredRoles: ['staff'],
      loadingDependencies: ['company', 'enrolment'],
      initialDataActions: [
        createAction(actions.getCompanies, {
          params: {
            format: 'reduced',
            q: 'metadata.liveness:true,lifecycle:verified|completed_setup',
          },
        }),
        createAction(actions.getEnrolments, { params: { format: 'reduced' } }),
      ],
    },
    users: {
      path: URLS.users,
      PageComponent: UsersListPage,
      requiredRoles: ['staff'],
      loadingDependencies: ['company'],
      initialDataActions: [
        createAction(actions.getCompanies, { params: { format: 'reduced' } }),
      ],
    },
    companiesAddCompany: {
      path: URLS.companiesAddCompany,
      PageComponent: AddCompanyPage,
      requiredRoles: ['staff'],
      loadingDependencies: [],
      initialDataActions: [],
    },
  },
  () => <Redirect to={URLS.home} />,
  undefined,
  URLS.login,
);

const useAppDispatch = createAppDispatchHook<RootState, AppDispatch>();

export { pages, useAppDispatch };

const App: FunctionComponent = function () {
  return (
    <AppWrapper>
      <Router />
    </AppWrapper>
  );
};

export default App;
